/**
 * Story 059 — Add-turbine-model modal (`/turbine-models?new=1`).
 *
 * - Required: Name, Manufacturer, Type (AC15).
 * - Save disabled until all three required fields are non-empty.
 * - Specifications sub-form starts empty; user grows it with
 *   "+ Add parameter" (UX brief §9 / AC16). The legacy per-baseType
 *   default-row dispatch is gone since assets/v1@1.3.0 (B-059 Phase 4 /
 *   2026-05-14) — every model carries the uniform parameter-array shape
 *   regardless of asset_type, so changing Type no longer rewrites
 *   Specifications.
 *
 * Body shape on submit (AC18):
 *   { name: {en}, manufacturer, manufacturerAbbreviation?, assetTypeId,
 *     specifications: [...], externalIds: [...], description?: {en} }
 * — NO id, NO assetModelCode, NO scope (server-derived), NO audit
 * (BE-assigned), NO baseType (replaced by assetTypeId).
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useCanEditResourceFor } from "@aeriosiq/ui";
import { useToast } from "../../account/ToastContext.js";
import {
  useCreateAssetModelMutation,
  useCreateGlobalAssetModelMutation,
  type AssetModelExternalId,
  type AssetModelSpecification,
  type AssetTypeSummary,
  type CreateAssetModelBody,
} from "../queries/index.js";
import { ExternalIdsField } from "../../sites/shared/ExternalIdsField.js";
import { SpecificationsField } from "../shared/SpecificationsField.js";

interface AddTurbineModelFormValues {
  name: string;
  manufacturer: string;
  manufacturerAbbreviation: string;
  assetTypeId: string;
  specifications: AssetModelSpecification[];
  externalIds: AssetModelExternalId[];
  description: string;
}

const EMPTY: AddTurbineModelFormValues = {
  name: "",
  manufacturer: "",
  manufacturerAbbreviation: "",
  assetTypeId: "",
  specifications: [],
  externalIds: [],
  description: "",
};

function dropEmptySpecifications(
  rows: AssetModelSpecification[],
): AssetModelSpecification[] {
  return rows
    .map((r) => ({
      parameter: r.parameter.trim(),
      unit: r.unit.trim(),
      value: r.value.trim(),
    }))
    .filter(
      (r) => r.parameter.length > 0 || r.unit.length > 0 || r.value.length > 0,
    )
    .filter((r) => r.parameter.length > 0);
}

function dropEmptyExternalIds(
  arr: AssetModelExternalId[],
): AssetModelExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function buildCreateBody(v: AddTurbineModelFormValues): CreateAssetModelBody {
  const body: CreateAssetModelBody = {
    name: { en: v.name.trim() },
    manufacturer: v.manufacturer.trim(),
    assetTypeId: v.assetTypeId,
  };
  if (v.manufacturerAbbreviation.trim().length > 0) {
    body.manufacturerAbbreviation = v.manufacturerAbbreviation.trim();
  }
  const specs = dropEmptySpecifications(v.specifications);
  if (specs.length > 0) body.specifications = specs;
  const ids = dropEmptyExternalIds(v.externalIds);
  if (ids.length > 0) body.externalIds = ids;
  if (v.description.trim().length > 0) {
    body.description = { en: v.description.trim() };
  }
  return body;
}

export interface AddTurbineModelDialogProps {
  open: boolean;
  onClose: () => void;
  /** Optional set of manufacturer suggestions sourced from the list
   *  rollup; surfaced via the Autocomplete's `options` so the user
   *  doesn't typo "Vestas" vs "vestas". Free-text — any string is
   *  acceptable. */
  manufacturerSuggestions?: readonly string[];
  /** Asset-type rollup from the list-response envelope; drives the Type
   *  picker (mirror Story 057's AssetTypeAutocomplete). */
  assetTypes?: readonly AssetTypeSummary[];
  /** SB2-W-05 — scope of the new row. Defaults to `'tenant'`. When
   *  `'global'`, the dialog routes to `useCreateGlobalAssetModelMutation`
   *  which sends `tenantId: null` (BE writes to
   *  `public.global_asset_models`; `platform_admin` only). */
  scope?: "tenant" | "global";
}

export function AddTurbineModelDialog({
  open,
  onClose,
  manufacturerSuggestions = [],
  assetTypes = [],
  scope = "tenant",
}: AddTurbineModelDialogProps): ReactElement {
  const { toast } = useToast();
  const createTenant = useCreateAssetModelMutation();
  const createGlobal = useCreateGlobalAssetModelMutation();
  const create = scope === "global" ? createGlobal : createTenant;
  // SB2-CC-04 — defence-in-depth banner. Gate key flips with scope.
  const permissionKey =
    scope === "global"
      ? "turbine_model.create_global"
      : "turbine_model.create_tenant";
  const canCreate = useCanEditResourceFor(permissionKey);
  const { control, register, handleSubmit, reset, watch, formState } =
    useForm<AddTurbineModelFormValues>({
      defaultValues: EMPTY,
      mode: "onChange",
    });

  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      reset(EMPTY);
      setSubmitError(null);
    }
  }, [open, reset]);

  const name = watch("name");
  const manufacturer = watch("manufacturer");
  const assetTypeId = watch("assetTypeId");

  const canSave =
    name.trim().length > 0 &&
    manufacturer.trim().length > 0 &&
    assetTypeId.length > 0 &&
    !create.isPending;

  async function onSubmit(values: AddTurbineModelFormValues): Promise<void> {
    setSubmitError(null);
    try {
      const body = buildCreateBody(values);
      const created = await create.mutateAsync(body);
      const code =
        typeof created.assetModelCode === "string"
          ? created.assetModelCode
          : String(created.assetModelCode);
      toast(
        scope === "global"
          ? `Global turbine model added (code ${code}).`
          : `Turbine model added (code ${code}).`,
      );
      onClose();
    } catch (err) {
      const errBody = (err as { body?: { code?: string; detail?: string } })
        .body;
      if (errBody?.detail?.startsWith("forbidden_field")) {
        toast("Cannot set this field — it's managed by the system.", "error");
      } else {
        toast("Couldn't add turbine model. Try again.", "error");
      }
      setSubmitError(errBody?.detail ?? "Unknown error");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="add-turbine-model-heading"
    >
      <DialogTitle id="add-turbine-model-heading">
        {scope === "global" ? "Add global turbine model" : "Add turbine model"}
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          {!canCreate ? (
            <Alert
              severity="info"
              variant="outlined"
              role="status"
              aria-live="polite"
              data-testid="add-modal-permission-banner"
              sx={{ mb: 2 }}
            >
              Your role does not allow adding a turbine model.
            </Alert>
          ) : null}
          <Stack spacing={2}>
            <TextField
              {...register("name", { required: true, minLength: 1 })}
              label="Name"
              required
              // Modals legitimately autofocus their first editable
              // field — same pattern as Sites Add.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              slotProps={{
                htmlInput: {
                  "aria-label": "Name",
                  "aria-required": true,
                  maxLength: 255,
                },
              }}
              fullWidth
            />
            <Controller
              name="manufacturer"
              control={control}
              rules={{ required: true, minLength: 1 }}
              render={({ field }) => (
                <Autocomplete<string, false, false, true>
                  freeSolo
                  options={[...manufacturerSuggestions]}
                  value={field.value}
                  onChange={(_e, next) => {
                    field.onChange(next ?? "");
                  }}
                  onInputChange={(_e, next) => {
                    field.onChange(next);
                  }}
                  renderInput={(params) => {
                    // exactOptionalPropertyTypes friction — same dance
                    // as Sites' CountryAutocomplete.
                    /* eslint-disable @typescript-eslint/no-explicit-any */
                    const passThrough: any = params;
                    return (
                      <TextField
                        {...passThrough}
                        label="Manufacturer"
                        required
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            "aria-label": "Manufacturer",
                            "aria-required": true,
                            maxLength: 100,
                          },
                        }}
                        fullWidth
                      />
                    );
                    /* eslint-enable */
                  }}
                />
              )}
            />
            <TextField
              {...register("manufacturerAbbreviation")}
              label="Manufacturer abbreviation"
              slotProps={{
                htmlInput: {
                  "aria-label": "Manufacturer abbreviation",
                  maxLength: 20,
                },
              }}
              fullWidth
            />
            <Controller
              name="assetTypeId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                const selected =
                  assetTypes.find((t) => t.id === field.value) ?? null;
                return (
                  <Autocomplete<AssetTypeSummary>
                    options={[...assetTypes]}
                    value={selected}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_e, next) => {
                      field.onChange(next ? next.id : "");
                    }}
                    renderInput={(params) => {
                      /* eslint-disable @typescript-eslint/no-explicit-any */
                      const passThrough: any = params;
                      return (
                        <TextField
                          {...passThrough}
                          label="Type"
                          required
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              "aria-label": "Type",
                              "aria-required": true,
                            },
                          }}
                          fullWidth
                        />
                      );
                      /* eslint-enable */
                    }}
                  />
                );
              }}
            />
            <SpecificationsField control={control} register={register} />
            <ExternalIdsField control={control} register={register} />
            <TextField
              {...register("description")}
              label="Description (EN)"
              multiline
              rows={3}
              slotProps={{
                htmlInput: {
                  "aria-label": "Description",
                  maxLength: 2000,
                },
              }}
              fullWidth
            />
            {submitError ? (
              <Box
                role="alert"
                sx={{
                  color: "error.main",
                  fontSize: 13,
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,69,58,0.10)"
                      : "rgba(255,59,48,0.06)",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                }}
              >
                {submitError}
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          {canCreate ? (
            <Button
              type="submit"
              variant="contained"
              disabled={!canSave || formState.isSubmitting}
              startIcon={
                create.isPending ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              Save
            </Button>
          ) : null}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
